import {Injectable} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {EMPTY, Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

import {EventService} from '@shared/event.service';
import {CacheRegistrationService} from './cache-registration.service';
import {OAuthService} from 'angular-oauth2-oidc';
import { environment } from '@environments/environment';

const STANDARD_API_DATA_VERIFIER_PATH = '/api/1.0.0/standard-api-data-verifier';

@Injectable()
export class CacheErrorInterceptor implements HttpInterceptor {
  // TODO: translateService doesn't work in interceptor now, let's check this at the newer version.
  private message = {};
  private cache = new Map<string, any>();

  constructor(private _router: Router,
              private _eventService: EventService,
              private _cacheRegistrationService: CacheRegistrationService,
              private _translateService: TranslateService,
              private _oauthService: OAuthService) {
    this.message = this._translateService.instant('error-message');
  }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError(errorResponse => this.oesCatchError(errorResponse, request)),
    );
  }

  private oesCatchError(errorResponse: HttpErrorResponse, request?: HttpRequest<any>) {
    const isCrmRequest = request?.url?.startsWith(`${ environment.serverUri }api/1.0.0/crm`);
    const isStandardApiVerifierRequest = request?.url?.includes(STANDARD_API_DATA_VERIFIER_PATH);

    // The validator renders provider failures in its own result panel. Do not replace its
    // error stream with the global authorization toast (or trigger a session logout).
    if (isStandardApiVerifierRequest) {
      return throwError(() => errorResponse);
    }

    if (!errorResponse || !errorResponse.status) {
      return throwError(errorResponse);
    }
    const errorDescription = errorResponse?.error?.error_description;
    switch (errorResponse?.status) {
      // Backend: IllegalArgumentException
      case (400):
        if (errorResponse?.error?.message?.includes('Could not locate record')) {
          // Session may already be invalidated - clear local tokens and start fresh login
          this._oauthService.logOut(true);
          this._oauthService.initLoginFlow();
          return EMPTY;
        }
        // Client session idle timeout. We can't renew your token after this timeout, bring a user to login
        if (errorResponse?.error?.error === 'invalid_grant') {
          if (errorDescription === 'Account disabled') {
            this._eventService.error(errorDescription + '. ' + this._translateService.instant('general-message.error.contact'));
          } else {
            this._eventService.error(this._translateService.instant('error-message.please-login'));
          }
          if (environment.name !== 'dev') {
            this._oauthService.logOut(true);     // clear local tokens + PKCE verifier, skip /logout
            this._oauthService.initLoginFlow();  // redirect to /auth with a fresh challenge
          }
          return EMPTY;
        }
        break;
      // Backend: Forbidden
      case (403):
        if (isCrmRequest) {
          const backendMessage = errorResponse?.error?.error || errorResponse?.error?.message || '';
          if (backendMessage.includes('missing DEVELOPER_CREDIT')) {
            this._router.navigate(['/oes/home']).then(() => {
              window.open('https://procure.odyssey.energy', '_blank');
            });
          } else if (backendMessage.includes('Org not invited')) {
            this._router.navigate(['/oes/home']).then(() => {
              window.open('https://procure.odyssey.energy', '_blank');
            });
          } else if (backendMessage.includes('missing isCredit')) {
            this._router.navigate(['/oes/home']).then(() => {
              window.open('https://procure.odyssey.energy', '_blank');
            });
          } else if (backendMessage.includes('missing isApprovedCredit')) {
            this._router.navigate(['/oes/credit/application']);
          } else if (backendMessage.includes('missing Zoho ID')) {
            this._router.navigate(['/oes/credit/application']);
          } else {
            this._eventService.error(this._translateService.instant('Not authorized'));
          }
          return EMPTY;
        } else {
          this._eventService.error(this._translateService.instant('Not authorized'));
          return EMPTY;
        }
      // Backend: security exception
      case (401):
        this._eventService.error(this._translateService.instant('Not authorized'));
        if (environment.name !== 'dev') {
          // Session may already be invalidated (e.g., post-logout or backchannel logout)
          // Clear local tokens without hitting IDP logout endpoint, then start fresh login
          this._oauthService.logOut(true);
          this._oauthService.initLoginFlow();
        }
        return EMPTY;
      // Backend: DataRetrievalFailureException
      // Backend: NoSuchElementException
      // Backend: IllegalStateException
      case (404):
        // do something
        break;
      // Backend: NullPointer exception
      // Backend: exceptions that are not mapped
      case (500):
        // do something
        break;
      // Backend: per-user export queue cap reached (ExportQueueFullException).
      // Intentionally passed through without a generic error toast so the shared-export
      // modal can render an inline queue-full message with the specific count. See
      // SharedExportModalComponent.onSubmitError.
      case (429):
        break;
    }
    return throwError(errorResponse);
  }

  private getUsernameFromRequest(request: HttpRequest<any>): string {
    const params = request.body.split('&').reduce((accum: Object, keyValue: string) => {
      const keyValueArray = keyValue.split('=');
      accum[keyValueArray[0]] = keyValueArray[1];
      return accum;
    }, {});
    return unescape(params.username);
  }
}
